import { motion } from 'framer-motion'
import { TimingMetrics } from '@/types/api'
import { formatTime } from '@/utils/helpers'

export interface PerformanceMetricsProps {
  timing: TimingMetrics
}

const PerformanceMetrics = ({ timing }: PerformanceMetricsProps) => {
  const metrics = [
    {
      label: 'Preprocess',
      value: timing.preprocess_ms,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      label: 'Inference',
      value: timing.inference_ms,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      label: 'Postprocess',
      value: timing.postprocess_ms,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
    },
    {
      label: 'Total',
      value: timing.total_ms,
      highlight: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      <h3 className="text-sm font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wider mb-3">
        Performance Metrics
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map((metric, index) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
            className={`
              p-4 rounded-lg border
              ${
                metric.highlight
                  ? 'bg-primary-500/10 border-primary-500/30 dark:bg-primary-500/5 dark:border-primary-500/20'
                  : 'bg-gray-50 border-gray-200 dark:bg-dark-800 dark:border-dark-700'
              }
            `}
          >
            <div className="flex items-center gap-2 mb-2 text-gray-500 dark:text-dark-400">
              {metric.icon}
              <span className="text-xs uppercase tracking-wider">
                {metric.label}
              </span>
            </div>
            <p
              className={`
                text-2xl font-bold
                ${metric.highlight ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-dark-50'}
              `}
            >
              {formatTime(metric.value)}
            </p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
}

export default PerformanceMetrics
